import type { Account, AccountType, JournalEntry, JournalLine } from '@nako/types';

export interface AccountBalance {
  account: Account;
  debit: number;
  credit: number;
  net: number; // debit - credit (positive = Dr balance)
}

export interface TrialBalance {
  rows: AccountBalance[];
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
}

export interface ProfitLoss {
  income: AccountBalance[];
  expense: AccountBalance[];
  totalIncome: number;
  totalExpense: number;
  netProfit: number;
}

export interface BalanceSheet {
  assets: AccountBalance[];
  liabilities: AccountBalance[];
  equity: AccountBalance[];
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  retainedEarnings: number; // net profit folded in
  isBalanced: boolean;
  outOfBalanceAmount: number; // totalAssets - (totalLiabilities + totalEquity)
}

interface Bundle {
  accounts: Account[];
  entries: JournalEntry[];
  lines: JournalLine[];
}

/** Net of debits - credits, signed by account type so the balance reads
 *  positive when "in the natural direction" of that account type. */
function naturalSign(type: AccountType, debit: number, credit: number): number {
  const dr = debit - credit;
  return type === 'asset' || type === 'expense' ? dr : -dr;
}

export function buildTrialBalance(b: Bundle, asOf?: Date): TrialBalance {
  const cutoffStr = ymd(asOf ?? new Date());
  const inWindow = b.entries.filter((e) => e.is_posted && e.entry_date <= cutoffStr);
  const entryIds = new Set(inWindow.map((e) => e.id));
  const lines = b.lines.filter((l) => entryIds.has(l.entry_id));

  const byAccount = new Map<string, { debit: number; credit: number }>();
  for (const l of lines) {
    const cur = byAccount.get(l.account_id) ?? { debit: 0, credit: 0 };
    cur.debit += l.debit;
    cur.credit += l.credit;
    byAccount.set(l.account_id, cur);
  }

  const rows: AccountBalance[] = b.accounts
    .map((a) => {
      const v = byAccount.get(a.id) ?? { debit: 0, credit: 0 };
      return {
        account: a,
        debit: round2(v.debit),
        credit: round2(v.credit),
        net: round2(v.debit - v.credit),
      };
    })
    .filter((r) => r.debit !== 0 || r.credit !== 0)
    .sort((a, b) => a.account.gl_code.localeCompare(b.account.gl_code));

  const totalDebit = round2(rows.reduce((s, r) => s + r.debit, 0));
  const totalCredit = round2(rows.reduce((s, r) => s + r.credit, 0));

  return {
    rows,
    totalDebit,
    totalCredit,
    balanced: totalDebit === totalCredit,
  };
}

export function buildProfitLoss(b: Bundle, from: Date, to: Date): ProfitLoss {
  const fromStr = ymd(from);
  const toStr = ymd(to);
  const inRange = b.entries.filter((e) => {
    if (!e.is_posted) return false;
    // entry_date is a 'YYYY-MM-DD' DATE string — compare as strings so a
    // local-time boundary can't shift a month-end entry into the wrong period
    // (new Date('YYYY-MM-DD') parses as UTC midnight). Round-2 finding #5.
    return e.entry_date >= fromStr && e.entry_date <= toStr;
  });
  const ids = new Set(inRange.map((e) => e.id));
  const lines = b.lines.filter((l) => ids.has(l.entry_id));

  const byAccount = new Map<string, { debit: number; credit: number }>();
  for (const l of lines) {
    const cur = byAccount.get(l.account_id) ?? { debit: 0, credit: 0 };
    cur.debit += l.debit;
    cur.credit += l.credit;
    byAccount.set(l.account_id, cur);
  }

  const expand = (type: AccountType): AccountBalance[] =>
    b.accounts
      .filter((a) => a.type === type)
      .map((a) => {
        const v = byAccount.get(a.id) ?? { debit: 0, credit: 0 };
        return {
          account: a,
          debit: round2(v.debit),
          credit: round2(v.credit),
          net: round2(naturalSign(a.type, v.debit, v.credit)),
        };
      })
      .filter((r) => r.net !== 0)
      .sort((a, b) => a.account.gl_code.localeCompare(b.account.gl_code));

  const income = expand('income');
  const expense = expand('expense');
  const totalIncome = round2(income.reduce((s, r) => s + r.net, 0));
  const totalExpense = round2(expense.reduce((s, r) => s + r.net, 0));

  return {
    income,
    expense,
    totalIncome,
    totalExpense,
    netProfit: round2(totalIncome - totalExpense),
  };
}

export function buildBalanceSheet(b: Bundle, asOf?: Date): BalanceSheet {
  const cutoff = asOf ?? new Date();
  const cutoffStr = ymd(cutoff);
  const inWindow = b.entries.filter((e) => e.is_posted && e.entry_date <= cutoffStr);
  const ids = new Set(inWindow.map((e) => e.id));
  const lines = b.lines.filter((l) => ids.has(l.entry_id));

  const byAccount = new Map<string, { debit: number; credit: number }>();
  for (const l of lines) {
    const cur = byAccount.get(l.account_id) ?? { debit: 0, credit: 0 };
    cur.debit += l.debit;
    cur.credit += l.credit;
    byAccount.set(l.account_id, cur);
  }

  const expand = (type: AccountType): AccountBalance[] =>
    b.accounts
      .filter((a) => a.type === type)
      .map((a) => {
        const v = byAccount.get(a.id) ?? { debit: 0, credit: 0 };
        return {
          account: a,
          debit: round2(v.debit),
          credit: round2(v.credit),
          net: round2(naturalSign(a.type, v.debit, v.credit)),
        };
      })
      .filter((r) => r.net !== 0)
      .sort((a, b) => a.account.gl_code.localeCompare(b.account.gl_code));

  const assets = expand('asset');
  const liabilities = expand('liability');
  const equity = expand('equity');

  // Compute year-to-date P&L and roll into retained earnings.
  const pl = buildProfitLoss(b, new Date('1970-01-01'), cutoff);

  const totalAssets = round2(assets.reduce((s, r) => s + r.net, 0));
  const totalLiabilities = round2(liabilities.reduce((s, r) => s + r.net, 0));
  const totalEquity = round2(equity.reduce((s, r) => s + r.net, 0) + pl.netProfit);

  const outOfBalanceAmount = round2(totalAssets - (totalLiabilities + totalEquity));

  return {
    assets,
    liabilities,
    equity,
    totalAssets,
    totalLiabilities,
    totalEquity,
    retainedEarnings: pl.netProfit,
    isBalanced: outOfBalanceAmount === 0,
    outOfBalanceAmount,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Local YYYY-MM-DD for a Date — avoids the UTC shift that
 *  `new Date('YYYY-MM-DD')` and `.toISOString()` introduce in UTC+2. */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
